/**
 * One product on Edit Tenant (Tenant Detail → Modules), drawn as Create
 * Tenant draws it.
 *
 * THE GAP (audit A, Phase 2 item 2, 6 Oct 2026). Create Tenant's product row
 * shows the name, the description, the default price (or why there is none),
 * the billing unit and whether it is selected. Edit Tenant's card showed a
 * name, a description that fell back to the internal `api_name`, a switch and
 * a raw `Source: PLAN` — no price, no unit — so an operator switching
 * Assistants or Cluvik AI on for an existing tenant could not see what it
 * costs or how it is billed.
 *
 * THE SAME FIVE FACTS, FROM THE SAME SERVER ROWS. `row` is the
 * `GET /commercial/presentation` row Create Tenant renders; the price block
 * is the same `DefaultPriceTag`. Live Chat is shown exactly as Create shows
 * it (owner, 24 Sep 2026): "Includes Live Chat — no separate line or price"
 * on Assistants, and "Included with Assistants" with no switch of its own.
 *
 * AND ONE FACT CREATE CANNOT HAVE: whether the product is LIVE in the
 * tenant's workspace. `runtime` is the `GET /tenants/{id}/product-state`
 * entry, read from `tenant_app_assignments` (and Cluvik AI's switch) — what
 * the runtime gates on — so a product Admin recorded that never landed says
 * so, with the retry beside it, instead of reading "enabled".
 *
 * Nothing on this card prints a product code.
 */
import React from 'react';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import DefaultPriceTag from './DefaultPriceTag';
import { productLabel } from './productLabels';
import { tenantLevelRow } from './CommercialConfigurator';

/** How a product came to be on, in an operator's words. An unknown value
 *  says nothing rather than printing itself. */
export const SOURCE_TEXT = {
  PLAN: "Part of the tenant's products",
  MANUAL_OVERRIDE: 'Switched on by an operator',
  TRIAL: 'Trial access',
  PROMO: 'Promotional access',
};

/** What the workspace will actually allow, from the product-state entry. */
export function runtimeNote(runtime, code) {
  if (!runtime) return null;
  if (runtime.state === 'not_landed') {
    if (code === 'ai_features' && runtime.cluvik_ai_switch === 'missing'
        && runtime.runtime_assignment === 'enabled') {
      return { tone: 'warning', retry: true,
        text: 'Not live: Cluvik AI has no workspace switch, so Assistants report it as off.' };
    }
    return { tone: 'warning', retry: true,
      text: 'Not live in the workspace. The workspace refuses it until it is switched on there.' };
  }
  if (runtime.state === 'runtime_only') {
    return { tone: 'warning', retry: false,
      text: 'Live in the workspace, but not recorded here as enabled.' };
  }
  if (runtime.state === 'live') {
    if (code === 'ai_features' && runtime.cluvik_ai_switch === 'off') {
      return { tone: 'muted', retry: false,
        text: "Live in the workspace; switched off by the tenant's administrator." };
    }
    return { tone: 'success', retry: false, text: 'Live in the workspace' };
  }
  return null;
}

const NOTE_CLASS = {
  warning: 'text-warning-text',
  success: 'text-success',
  muted: 'text-muted-foreground',
};

export default function TenantProductCard({
  code, module, row, labels, enabled, includedWith, runtime, enabledSource,
  onToggle, onReapply, reapplying, onRecheck, rechecking, recheckError,
}) {
  // The server's names first (catalogue row, label map, module registry);
  // a title-cased code only as the very last resort.
  const name = row?.label || labels?.[code] || module?.name || productLabel(code, labels);
  // The product's own description, never its identifier.
  const description = row?.description || module?.description || null;
  const note = enabled || runtime ? runtimeNote(runtime, code) : null;
  const sourceText = enabled ? SOURCE_TEXT[enabledSource] : null;
  const selectable = !!row?.selectable;

  return (
    <div className={`rounded-lg border p-4 ${enabled ? 'border-primary/40 bg-primary/5' : 'border-input bg-card'}`}
         data-testid={`product-card-${code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
            <span data-testid={`product-name-${code}`}>{name}</span>
            {row && !selectable && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border"
                    data-testid={`included-badge-${code}`}>
                Included
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
              enabled ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-input'}`}
                  data-testid={`product-state-${code}`}>
              {enabled ? 'Enabled' : 'Not enabled'}
            </span>
          </div>
          {description && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid={`product-description-${code}`}>
              {description}
            </p>
          )}
        </div>
        {selectable && row.default_price && (
          <DefaultPriceTag code={code} price={row.default_price}
                           onRecheck={onRecheck} rechecking={rechecking}
                           recheckError={recheckError} />
        )}
      </div>

      {selectable && tenantLevelRow(row) && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`unit-${code}`}>
          Whole workspace
        </p>
      )}

      {/* WHAT THE PRODUCT BRINGS WITH IT, as Create Tenant says it. */}
      {(row?.includes || []).length > 0 && (
        <p className="mt-1 text-xs text-foreground" data-testid={`includes-${code}`}>
          Includes {row.includes.map((i) => i.label).join(', ')} — no separate
          line or price
        </p>
      )}

      {note && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`product-runtime-${code}`}>
          <span className={`text-xs ${NOTE_CLASS[note.tone]}`}>{note.text}</span>
          {note.retry && onReapply && (
            <Button type="button" variant="outline" size="sm" onClick={onReapply}
                    disabled={reapplying} data-testid={`product-reapply-${code}`}>
              {reapplying ? 'Switching on…' : 'Switch on in workspace'}
            </Button>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
        <span className="text-xs text-muted-foreground" data-testid={`product-source-${code}`}>
          {sourceText || ''}
        </span>
        {/* INCLUDED WITH ANOTHER PRODUCT (Live Chat, part of Assistants —
            owner, 24 Sep 2026; Form Builder and Survey Builder, replaced by
            Engage — owner, 27 Sep 2026). No switch of its own. */}
        {includedWith ? (
          <span className="text-[11px] text-muted-foreground text-right"
                data-testid={`included-with-${code}`}>
            Included with {includedWith}
          </span>
        ) : (
          <Switch checked={!!enabled} onCheckedChange={onToggle}
                  aria-label={`${enabled ? 'Disable' : 'Enable'} ${name}`}
                  data-testid={`product-switch-${code}`} />
        )}
      </div>
    </div>
  );
}
